/**
 * Currency formatting utilities
 */

export const CURRENCY_CODE = 'GHS';
export const CURRENCY_SYMBOL = 'GHS';
export const DECIMAL_PLACES = 2;

/**
 * Format amount with exactly two decimal places
 * @param amount - The amount to format
 * @param includeSymbol - Whether to include the currency symbol
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | string | undefined | null,
  includeSymbol: boolean = true
): string => {
  if (amount === undefined || amount === null || amount === '') {
    return includeSymbol ? `${CURRENCY_SYMBOL} 0.00` : '0.00';
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return includeSymbol ? `${CURRENCY_SYMBOL} 0.00` : '0.00';
  }

  const formatted = numAmount.toFixed(DECIMAL_PLACES);
  
  return includeSymbol ? `${CURRENCY_SYMBOL} ${formatted}` : formatted;
};

/**
 * Format amount without currency symbol (for internal use)
 * @param amount - The amount to format
 * @returns Formatted amount string with two decimal places
 */
export const formatAmount = (amount: number | string | undefined | null): string => {
  return formatCurrency(amount, false);
};

/**
 * Parse currency string back to number
 * @param currencyString - The currency string to parse
 * @returns Parsed number
 */
export const parseCurrency = (currencyString: string): number => {
  // Remove currency symbol and convert to number
  const cleaned = currencyString.replace(/[^\d.-]/g, '');
  return parseFloat(cleaned) || 0;
};

/**
 * Format amount for display in transaction history
 * @param amount - The amount to format
 * @param type - Transaction type ('credit' or 'debit')
 * @returns Formatted currency string with appropriate sign
 */
export const formatTransactionAmount = (
  amount: number | string | undefined | null,
  type: 'credit' | 'debit' = 'credit'
): string => {
  const formatted = formatCurrency(amount, true);
  return type === 'credit' ? `+${formatted}` : `-${formatted}`;
};
